import React, { useState, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { FaExclamationTriangle } from 'react-icons/fa';
import { Button } from './Button';
import './DangerousActionDialog.scss';

/**
 * DangerousActionDialog - A heavily protected confirmation dialog for dangerous operations
 * 
 * Features:
 * - Requires typing confirmation text (default "DELETE")
 * - Checkbox "I understand this cannot be undone"
 * - Red/danger styling throughout
 * - Shows impact information (e.g., "This will delete X properties")
 * 
 * @param {boolean} show - Show/hide modal
 * @param {function} onClose - Close handler
 * @param {function} onConfirm - Confirm handler (called when all requirements met)
 * @param {string} title - Dialog title
 * @param {string} message - Warning message
 * @param {string} confirmationText - Text user must type (default "DELETE")
 * @param {React.ReactNode} impactInfo - Additional impact information to display
 * @param {boolean} loading - Loading state during operation
 * @param {string} confirmButtonText - Text for confirm button (default "Delete")
 */
export const DangerousActionDialog = ({
  show,
  onClose,
  onConfirm,
  title = "Confirm Dangerous Action",
  message,
  confirmationText = "DELETE",
  impactInfo,
  loading = false,
  confirmButtonText = "Delete",
}) => {
  const [inputValue, setInputValue] = useState('');
  const [understood, setUnderstood] = useState(false);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (show) {
      setInputValue('');
      setUnderstood(false);
    }
  }, [show]);

  const isValid = inputValue === confirmationText && understood;

  const handleConfirm = () => {
    if (isValid && !loading) {
      onConfirm();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && isValid && !loading) {
      handleConfirm();
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onClose} 
      centered
      className="dangerous-action-dialog"
    >
      <Modal.Header closeButton className="border-danger">
        <Modal.Title className="text-danger d-flex align-items-center gap-2">
          <FaExclamationTriangle />
          {title}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <div className="alert alert-danger d-flex align-items-start gap-2 mb-3">
          <FaExclamationTriangle className="mt-1 flex-shrink-0" />
          <div>
            <strong>Warning:</strong> {message}
          </div>
        </div>

        {impactInfo && (
          <div className="impact-info mb-3 p-3 bg-light rounded border border-danger">
            {impactInfo}
          </div>
        )}

        <Form.Group className="mb-3">
          <Form.Label className="fw-bold">
            Type <code className="text-danger">{confirmationText}</code> to confirm:
          </Form.Label>
          <Form.Control
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={confirmationText}
            autoFocus
            disabled={loading}
            className={inputValue && inputValue !== confirmationText ? 'is-invalid' : ''}
          />
          {inputValue && inputValue !== confirmationText && (
            <Form.Text className="text-danger">
              Text does not match
            </Form.Text>
          )}
        </Form.Group>

        <Form.Group>
          <Form.Check
            type="checkbox"
            id="understand-checkbox"
            label={<strong>I understand this action cannot be undone</strong>}
            checked={understood}
            onChange={(e) => setUnderstood(e.target.checked)}
            disabled={loading}
            className="text-danger"
          />
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleConfirm}
          disabled={!isValid || loading}
          loading={loading}
        >
          {confirmButtonText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DangerousActionDialog;
